import React from 'react';
import { PlayerScore } from '../types';

interface ScoreboardOverlayProps {
  isOpen: boolean;
  roomCode: string;
  playerScores: Record<string, PlayerScore>;
  playerId: string | null;
  playerOnlineStatus?: Record<string, { name: string; isOnline: boolean }>;
  onClose: () => void;
}

const getRankBadge = (rank: number) => {
  if (rank === 1) {
    return { emoji: '🥇', bgColor: '#FFD700' };
  }
  if (rank === 2) {
    return { emoji: '🥈', bgColor: '#C0C0C0' };
  }
  if (rank === 3) {
    return { emoji: '🥉', bgColor: '#CD7F32' };
  }
  return { emoji: '🏅', bgColor: 'rgba(255, 255, 255, 0.2)' };
};

const ScoreChip: React.FC<{ icon: string; label: string; value: number }> = ({ icon, label, value }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'rgba(255, 255, 255, 0.15)',
    padding: '4px 8px',
    borderRadius: '8px'
  }}>
    <span style={{ fontSize: '14px' }}>{icon}</span>
    <span style={{
      color: 'white',
      fontSize: '12px',
      fontWeight: '600'
    }}>
      {value}
    </span>
  </div>
);

export const ScoreboardOverlay: React.FC<ScoreboardOverlayProps> = ({
  isOpen,
  roomCode,
  playerScores,
  playerId,
  playerOnlineStatus,
  onClose
}) => {
  if (!isOpen) return null;

  const sortedScores = Object.values(playerScores)
    .slice()
    .sort((a, b) => b.totalPoints - a.totalPoints);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(180deg, #6366f1 0%, #a855f7 55%, #ec4899 100%)',
        zIndex: 1200,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '28px 18px',
        overflowY: 'auto'
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close scoreboard"
        style={{
          position: 'absolute',
          top: '24px',
          left: '20px',
          width: '42px',
          height: '42px',
          borderRadius: '21px',
          border: 'none',
          background: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          fontSize: '18px',
          fontWeight: 700,
          cursor: 'pointer'
        }}
      >
        X
      </button>

      <div style={{ width: '100%', maxWidth: '520px' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          paddingTop: '32px'
        }}>
          <div style={{ fontSize: '36px' }}>🏆</div>
          <h1 style={{
            color: 'white',
            margin: 0,
            fontSize: '32px',
            fontWeight: 700
          }}>
            Scoreboard
          </h1>
          <div style={{
            padding: '8px 16px',
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontSize: '13px'
          }}>
            Room: {roomCode}
          </div>
        </div>

        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {sortedScores.length === 0 && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              padding: '18px',
              color: 'white',
              textAlign: 'center'
            }}>
              No scores yet.
            </div>
          )}

          {sortedScores.map((score, index) => {
            const rank = index + 1;
            const isCurrentPlayer = score.id === playerId;
            const badge = getRankBadge(rank);
            const isOnline = playerOnlineStatus?.[score.id]?.isOnline ?? true;

            return (
              <div
                key={score.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '16px 18px',
                  borderRadius: '18px',
                  background: isCurrentPlayer ? 'rgba(16, 185, 129, 0.25)' : 'rgba(192, 132, 252, 0.25)',
                  border: isCurrentPlayer ? '2px solid rgba(16, 185, 129, 0.7)' : '1px solid rgba(192, 132, 252, 0.5)',
                  opacity: isOnline ? 1 : 0.6
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  {/* Rank Badge with Emoji */}
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {badge.emoji}
                  </div>

                  {/* Player Info */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
                        {score.playerName || 'Player'}
                      </span>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: isOnline ? '#10b981' : '#9ca3af',
                        marginTop: '2px'
                      }} />
                      {isCurrentPlayer && (
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          background: '#10B981',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '6px'
                        }}>
                          you
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <ScoreChip icon='💡' label='Hint' value={score.hintOnSuccessfulContact} />
                      <ScoreChip icon='🤚' label='Contact' value={score.successfulContact} />
                      <ScoreChip icon='🛡️' label='Defense' value={score.masterDefence} />
                    </div>
                  </div>
                </div>

                {/* Points */}
                <div style={{ textAlign: 'right', minWidth: '70px' }}>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 700 }}>
                    {score.totalPoints}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>pts</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scoring Legend */}
        <div style={{
          marginTop: '24px',
          paddingBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px'
        }}>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '1.5px',
            margin: 0
          }}>
            SCORING
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            width: '100%',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              color: 'white'
            }}>
              <span style={{ fontSize: '24px' }}>💡</span>
              <span style={{ fontSize: '14px', fontWeight: '700' }}>Hint</span>
              <span style={{ fontSize: '14px', fontWeight: '700', opacity: 0.8 }}>+10</span>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              color: 'white'
            }}>
              <span style={{ fontSize: '24px' }}>🤚</span>
              <span style={{ fontSize: '14px', fontWeight: '700' }}>Contact</span>
              <span style={{ fontSize: '14px', fontWeight: '700', opacity: 0.8 }}>+15</span>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              color: 'white'
            }}>
              <span style={{ fontSize: '24px' }}>🛡️</span>
              <span style={{ fontSize: '14px', fontWeight: '700' }}>Defense</span>
              <span style={{ fontSize: '14px', fontWeight: '700', opacity: 0.8 }}>+20</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
